import { OT_MAKE_PAYMENT, OT_PAST_DUE_PAYMENT } from '../helpers/apis';
import apiClient from '../middleware/apiClient';
import { COTTON_OT_MAKE_PAYMENT, COTTON_OT_PAST_DUE_PAYMENT } from '../helpers/cottonApis';
import { isCottonSession as getCottonSession } from '../helpers/featureFlags/cotton';
import { getErrorFromResponse } from '../helpers/errors';
import { flagEnabled } from '../helpers/featureFlags';
import { buildInstrument } from '../helpers/payments';
import { XM_PAYMENT_FAILED, XM_PAYMENT_IMM_COMPLETE } from '../helpers/channelTracking';
import { sendChannelTracking } from './channelTracking';
import { dispatchSendErrorEvent } from './adobe';
import { cleanDollarAmount } from '../helpers/validation/amount';
import { formatExpirationDate } from '../helpers/date';
import { getCcTypeCode } from '../helpers/ccType';

// Submit XM Payment
export const SUBMIT_XM_PAYMENT = 'SUBMIT_XM_PAYMENT';
export const SUBMIT_XM_PAYMENT_SUCCESS = 'SUBMIT_XM_PAYMENT_SUCCESS';
export const SUBMIT_XM_PAYMENT_FAILURE = 'SUBMIT_XM_PAYMENT_FAILURE';

// POST One Time Payment
export const POST_XM_ONE_TIME_PAY = 'POST_XM_ONE_TIME_PAY';
export const POST_XM_ONE_TIME_PAY_SUCCESS = 'POST_XM_ONE_TIME_PAY_SUCCESS';
export const POST_XM_ONE_TIME_PAY_FAILURE = 'POST_XM_ONE_TIME_PAY_FAILURE';

export const REVIEW_XM_PAYMENT = 'REVIEW_XM_PAYMENT';

// Mobile-specific review data - simplified for XM flow
// Only includes: amount, date, payment method (instrument), and email
export const setReviewMobileData = (formData, instruments = [], email) => {
  const {
    paymentAmountOption, customAmount,
  } = formData;

  const amount = paymentAmountOption === 'custom' ? cleanDollarAmount(customAmount) : paymentAmountOption;
  const { instrument } = buildInstrument(formData, instruments);

  return {
    ...formData,
    amount,
    instrument,
    email,
  };
};

function dispatchReviewXMPayment(data) {
  return {
    type: REVIEW_XM_PAYMENT,
    payload: data,
  };
}

export const reviewXMPayment = values => async (dispatch, getState) => {
  const {
    instruments: { instruments: { instruments } },
    auth: { macaroon: { preferredEmail, alternateEmail } = {} },
  } = getState();

  const userData = {
    ...values,
  };

  const reviewData = setReviewMobileData(
    userData,
    instruments,
    preferredEmail || alternateEmail,
  );

  return dispatch(dispatchReviewXMPayment(reviewData));
};

function dispatchSubmitXMPayment(data) {
  return {
    type: SUBMIT_XM_PAYMENT,
    payload: data,
  };
}

function dispatchSubmitXMPaymentFailure(error) {
  return {
    type: SUBMIT_XM_PAYMENT_FAILURE,
    payload: error,
  };
}

function dispatchSubmitXMPaymentSuccess(confirm) {
  return {
    type: SUBMIT_XM_PAYMENT_SUCCESS,
    payload: confirm,
  };
}

function dispatchPostXMOneTimePaySuccess(payment) {
  return {
    type: POST_XM_ONE_TIME_PAY_SUCCESS,
    payload: payment,
  };
}

function dispatchPostXMOneTimePayFailure(error) {
  return {
    type: POST_XM_ONE_TIME_PAY_FAILURE,
    payload: error,
  };
}

export const postXMOneTimePay = (data, isPastDue) => async (dispatch) => {
  const isCottonSession = getCottonSession();
  try {
    const response = await dispatch(
      dispatchPostXMOneTimePay(data, isPastDue, isCottonSession),
    ).payload;
    dispatch(dispatchPostXMOneTimePaySuccess(response));
    dispatch(sendChannelTracking({
      id: XM_PAYMENT_IMM_COMPLETE,
      interactionType: 'XM_BILLING_PAYMENTS',
    }));
    return response;
  } catch (error) {
    dispatch(sendChannelTracking({
      id: XM_PAYMENT_FAILED,
      error,
      interactionType: 'XM_BILLING_PAYMENTS',
    }));
    dispatch(dispatchPostXMOneTimePayFailure(error));
    throw error;
  }
};

function dispatchPostXMOneTimePay(data, isPastDue, isCottonSession = false) {
  const makePaymentEndpoint = isCottonSession ? COTTON_OT_MAKE_PAYMENT : OT_MAKE_PAYMENT;
  const pastDueEndpoint = isCottonSession ? COTTON_OT_PAST_DUE_PAYMENT : OT_PAST_DUE_PAYMENT;
  const paymentEndpoint = isPastDue ? pastDueEndpoint : makePaymentEndpoint;
  return {
    type: POST_XM_ONE_TIME_PAY,
    payload: apiClient.fetch(paymentEndpoint, { method: 'POST', body: JSON.stringify(data) }),
  };
}

export const submitXMPayment = ({
  autopay,
  transactionMetadata,
  isPastDue,
}) => async (dispatch, getState) => {
  const {
    user: { user: { customerGuid } },
    mobilePayment,
    currentXMAutopayInstrument,
  } = getState();
  const { token: currentInstrumentToken } = currentXMAutopayInstrument;
  const { review } = mobilePayment;

  const {
    amount,
    token: selectedToken,
    paymentMethodOption,
    billingAddress,
    walletId,
  } = review;

  dispatch(dispatchSubmitXMPayment(review));

  const paymentData = {
    amount,
    walletId: walletId || customerGuid,
  };

  if (flagEnabled('xmTxnDataEnabled')) {
    // Enhanced transaction data for Payment Fraud
    paymentData.ipAddress = transactionMetadata.ipAddress;
    paymentData.countryOfOrigin = transactionMetadata.countryOfOrigin;
    paymentData.comcastNetwork = transactionMetadata.onNetwork;
  }

  // If selected payment method is not same as the current XM autopay instrument
  // then we need to send additional payload data
  if (currentInstrumentToken !== selectedToken) {
    const { last4DigitsCardNumber, expirationDate, cardType } = paymentMethodOption;
    const { firstName = '', lastName = '' } = billingAddress;

    paymentData.saveInstrument = autopay;
    paymentData.paymentInstrument = {
      name: `${firstName} ${lastName}`,
      ccLastFour: last4DigitsCardNumber,
      expiresIn: formatExpirationDate(expirationDate), // e.g., '2030-04'
      ccType: getCcTypeCode(cardType), // e.g., '001',
      token: selectedToken,
      billingAddress,
    };
  }

  try {
    // make one time payment call
    const response = await dispatch(postXMOneTimePay(paymentData, isPastDue));
    // If response is blank, throw an error
    if (!response) {
      throw new Error('Error submitting payment');
    }

    const {
      authCode, paymentTransactionId, billAuthCode, billPaymentTransactionId,
    } = response;

    const authorizationNumber = isPastDue ? billAuthCode : authCode;
    const confirmationNumber = isPastDue ? billPaymentTransactionId : paymentTransactionId;

    const confirm = {
      ...review,
      ...response,
      autopay,
      // Include confirmation & authorization numbers.
      confirmationNumber: confirmationNumber && `#${confirmationNumber}`,
      authorizationNumber: authorizationNumber && `#${authorizationNumber}`,
    };

    dispatch(dispatchSubmitXMPaymentSuccess(confirm));
    return confirm;
  } catch (error) {
    // process the payment and report payment errors.
    const errorDetail = getErrorFromResponse(error);
    dispatch(dispatchSubmitXMPaymentFailure(errorDetail));
    dispatch(dispatchSendErrorEvent(errorDetail));
    throw error;
  }
};
